import { FormInstance } from "antd";
import { ConditionLogic, QueryOperation } from "../../code/search-query/search-query";
import { DynamicControl } from "../../controls/DynamicForm.default";
import { InputObject } from "../../controls/default";
import {
  CalendarInvitation,
  Company,
  CompanyCollaborator,
  Department,
  EventParticipantRole,
  MAX_PAGE_SIZE,
  PlatformUser,
} from "../../repository/data.default";
import {
  addCalendarCompanyInvitations,
  addCalendarDepartmentInvitations,
  addCalendarUserInvitations,
  getCalendarEventTypes,
  getCompanies,
  getCompanyCollaborators,
  getCompanyDepartments,
  getPlatformUsers,
} from "../../repository/data.repository";
import { dateTimeFormat } from "../../utils/date";
import { retry } from "../../utils/http";
import { nameOf } from "../../utils/typescript";

// Shared by CalendarEventDialog (admin) and MyCalendarEventDialog (personal): the event form's
// option requests, the role-aware invitation fan-out, the core field schema, and small display
// helpers. Lifting these out of both dialogs removes ~600 lines of copy-paste.

// A staged (newly picked) or loaded participant carried in a dialog's invitation list. For loaded
// invitations `dataItem.uid` is the invite uid (used for removal); `__role`/`__type` drive display
// and the fan-out endpoint.
export type ParticipantItem = InputObject<{
  readonly uid?: string;
  readonly __type?: "user" | "company" | "department";
  readonly __role?: EventParticipantRole;
  readonly __name?: string;
  // For user targets: a plain company member vs an assigned affiliate (drives the picker's type tag).
  readonly __kind?: "member" | "affiliate";
  // For department-scoped user targets: the member's department (lets a dialog drop staged attendees
  // whose department was removed from the event).
  readonly __departmentUid?: string;
  // For department-scoped user targets: the member's company role name (Employee, Manager, …).
  readonly __roleName?: string;
}>;

// One group of pickable invitation targets ("Companies" / "Departments" / "Users") — the grouped
// shape both the ComboBox control and ParticipantTargetSelect understand.
export type TargetGroup = {
  readonly display: string;
  readonly value: ParticipantItem[];
};

const startsWith = (field: string, search: string) => ({
  field,
  operation: QueryOperation.StartsWith,
  values: [search],
});

// Event types owned by their own flow and not offered as ad-hoc calendar events. Mirrors the backend's
// reserved codes (CalendarEventTypeCodes) — e.g. a Counselling Session is created from the Counselling module
// (which sets the event's origin), so it must not be pickable here. Add future reserved codes to the set.
const RESERVED_EVENT_TYPE_CODES = new Set<string>(["CounsellingSession", "TrainingSession"]);

const equals = (field: string, value: string) => ({
  field,
  operation: QueryOperation.Equal,
  values: [value],
});

const dedupeByValue = (items: ParticipantItem[]): ParticipantItem[] =>
  Array.from(new Map(items.map((i) => [i.value, i])).values());

// A regular company member (employee/manager) as a participant item.
const memberItem = (d: PlatformUser, role: EventParticipantRole): ParticipantItem => ({
  display: d.userName,
  subDisplay: d.companyName,
  value: d.uid,
  dataItem: {
    __type: "user",
    __role: role,
    __kind: "member",
    __name: [d.companyName, "Member"].filter(Boolean).join(" · "),
  },
});

// An affiliate (consultant/collaborator) assigned to the company, as a participant item.
const affiliateItem = (
  a: CompanyCollaborator,
  role: EventParticipantRole,
  kind: string,
): ParticipantItem => ({
  display: a.userUserName,
  subDisplay: [a.companyName, kind].filter(Boolean).join(" · "),
  value: a.userUid,
  dataItem: {
    __type: "user",
    __role: role,
    __kind: "affiliate",
    __name: [a.companyName, kind].filter(Boolean).join(" · "),
  },
});

const companyTargetItem = (d: Company): ParticipantItem => ({
  display: d.name,
  value: d.uid,
  dataItem: { __name: "Company", __type: "company", __role: EventParticipantRole.Attendee },
});

const departmentTargetItem = (d: Department): ParticipantItem => ({
  display: d.name,
  subDisplay: d.companyName,
  value: d.uid,
  dataItem: { __name: "Department", __type: "department", __role: EventParticipantRole.Attendee },
});

// A company's members + its assigned affiliates, as participant items of the given role. The
// CompanyCollaborators endpoint returns all affiliates assigned to the company (consultants + collaborators).
// Shared by the event dialogs' pickers and the training manage dialog's participant picker.
export const companyParticipants = (
  companyUid: string,
  search: string,
  role: EventParticipantRole,
) =>
  retry(() =>
    Promise.all([
      getPlatformUsers({
        searchQuery: {
          conditions: [
            startsWith(nameOf<PlatformUser>("userName"), search),
            equals(nameOf<PlatformUser>("companyUid"), companyUid),
          ],
        },
        pageNumber: 0,
        pageSize: 50,
      }),
      getCompanyCollaborators({
        searchQuery: {
          conditions: [
            startsWith(nameOf<CompanyCollaborator>("userUserName"), search),
            equals(nameOf<CompanyCollaborator>("companyUid"), companyUid),
          ],
        },
        pageNumber: 0,
        pageSize: 50,
      }),
    ]),
  ).then(([members, affiliates]) =>
    dedupeByValue([
      ...members.data.map((d) => memberItem(d, role)),
      ...affiliates.data.map((a) => affiliateItem(a, role, "Affiliate")),
    ]),
  );

// Platform-wide invitation targets, grouped Companies / Departments / Users — the admin-only path
// used when no company scopes the picker (the backend still enforces targeting).
export const platformWideTargets = (
  search: string,
  role: EventParticipantRole = EventParticipantRole.Attendee,
): Promise<TargetGroup[]> =>
  retry(() =>
    Promise.all([
      getCompanies({
        searchQuery: { conditions: [startsWith(nameOf<Company>("name"), search)] },
        pageNumber: 0,
        pageSize: 50,
      }),
      getCompanyDepartments({
        searchQuery: { conditions: [startsWith(nameOf<Department>("name"), search)] },
        pageNumber: 0,
        pageSize: 50,
      }),
      getPlatformUsers({
        searchQuery: { conditions: [startsWith(nameOf<PlatformUser>("userName"), search)] },
        pageNumber: 0,
        pageSize: 50,
      }),
    ]),
  ).then(([companies, departments, members]) => [
    { display: "Companies", value: companies.data.map(companyTargetItem) },
    { display: "Departments", value: departments.data.map(departmentTargetItem) },
    { display: "Users", value: members.data.map((d) => memberItem(d, role)) },
  ]);

// The grouped invitation targets scoped to one company: the company itself (whole-company invite),
// its departments, and its members + assigned affiliates. The single loader behind every attendee /
// participant picker (calendar event dialogs, training participants).
export const companyTargets = (
  companyUid: string,
  search: string,
  role: EventParticipantRole = EventParticipantRole.Attendee,
): Promise<TargetGroup[]> =>
  Promise.all([
    retry(() =>
      getCompanies({
        searchQuery: { conditions: [equals(nameOf<Company>("uid"), companyUid)] },
        pageNumber: 0,
        pageSize: 5,
      }),
    ),
    retry(() =>
      getCompanyDepartments({
        searchQuery: {
          conditions: [
            startsWith(nameOf<Department>("name"), search),
            equals(nameOf<Department>("companyUid"), companyUid),
          ],
        },
        pageNumber: 0,
        pageSize: 50,
      }),
    ),
    companyParticipants(companyUid, search, role),
  ]).then(([companies, departments, users]) => [
    { display: "Companies", value: companies.data.map(companyTargetItem) },
    { display: "Departments", value: departments.data.map(departmentTargetItem) },
    { display: "Users", value: users },
  ]);

// Only the members of the given departments (users-only group) — the attendee picker of an event that
// targets specific departments. No departments -> nothing to offer. Affiliates are left out: they have
// no department membership.
// "First Last" (falls back to the username when neither is set).
const fullName = (d: PlatformUser): string =>
  [d.firstName, d.lastName].filter(Boolean).join(" ") || d.userName;

const departmentMembers = (
  companyUid: string,
  departmentUids: string[],
  search: string,
  role: EventParticipantRole,
  pageSize: number,
): Promise<ParticipantItem[]> =>
  !departmentUids.length
    ? Promise.resolve([])
    : retry(() =>
        getPlatformUsers({
          searchQuery: {
            conditions: [
              equals(nameOf<PlatformUser>("companyUid"), companyUid),
              {
                field: nameOf<PlatformUser>("departmentUid"),
                operation: QueryOperation.In,
                values: departmentUids,
              },
            ],
            // The options show full names + role tags, so the search matches first name, last name,
            // username or role name (Employee, Manager, …).
            groups: search
              ? [
                  {
                    logic: ConditionLogic.Or,
                    conditions: [
                      startsWith(nameOf<PlatformUser>("firstName"), search),
                      startsWith(nameOf<PlatformUser>("lastName"), search),
                      startsWith(nameOf<PlatformUser>("userName"), search),
                      startsWith(nameOf<PlatformUser>("roleName"), search),
                    ],
                  },
                ]
              : undefined,
          },
          pageNumber: 0,
          pageSize,
        }),
      ).then((members) =>
        members.data.map((d) => ({
          display: fullName(d),
          subDisplay: d.departmentName,
          value: d.uid,
          dataItem: {
            __type: "user" as const,
            __role: role,
            __kind: "member" as const,
            __name: d.departmentName,
            __departmentUid: d.departmentUid,
            __roleName: d.roleName,
          },
        })),
      );

// Only the members of the given departments (users-only group) — the attendee picker of an event that
// targets specific departments. No departments -> nothing to offer. Affiliates are left out: they have
// no department membership.
export const departmentUserTargets = (
  companyUid: string,
  departmentUids: string[],
  search: string,
  role: EventParticipantRole = EventParticipantRole.Attendee,
): Promise<TargetGroup[]> =>
  departmentMembers(companyUid, departmentUids, search, role, 50).then((users) =>
    users.length ? [{ display: "Users", value: users }] : [],
  );

// Every member of the given departments (the "Add all" action), up to the API's max page size.
export const allDepartmentMembers = (
  companyUid: string,
  departmentUids: string[],
  role: EventParticipantRole = EventParticipantRole.Attendee,
): Promise<ParticipantItem[]> =>
  departmentMembers(companyUid, departmentUids, "", role, MAX_PAGE_SIZE);

export function participantRole(item: ParticipantItem): EventParticipantRole {
  return item.dataItem?.__role ?? EventParticipantRole.Attendee;
}

export function participantTagColor(role?: EventParticipantRole): string {
  return role === EventParticipantRole.Host ? "gold" : "blue";
}

// Map a loaded invitation to a list item, tagged by its persisted role.
export function mapInvitation(item: CalendarInvitation): ParticipantItem {
  return {
    display: item.userUserName,
    value: item.userUid,
    dataItem: {
      uid: item.uid,
      __type: "user",
      __role: item.role,
      __name: [item.eventCompanyName, item.role].filter(Boolean).join(" · "),
    },
  };
}

// The option requests for the event form. `departmentsRequest` and `hostsRequest` scope to the company
// currently picked in `form` — the host picker offers that company's members + its assigned affiliates
// (mirroring the backend), falling back to platform-wide only when no company is set. The attendee
// picker is `ParticipantTargetSelect`, fed by `companyTargets`/`platformWideTargets` above.
export function buildCalendarEventRequests(form: FormInstance<any>) {
  const typesRequest = (search: string) =>
    retry(() =>
      getCalendarEventTypes({
        searchQuery: { conditions: [startsWith("name", search)] },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data
        .filter((d) => !RESERVED_EVENT_TYPE_CODES.has(d.code ?? ""))
        .map((d) => ({ display: d.name, value: d.uid, dataItem: d })),
    );

  const companiesRequest = (search: string) =>
    retry(() =>
      getCompanies({
        searchQuery: { conditions: [startsWith(nameOf<Company>("name"), search)] },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((d) => ({ display: d.name, value: d.uid, dataItem: d })),
    );

  const departmentsRequest = (search: string) => {
    const conditions: any[] = [startsWith(nameOf<Department>("name"), search)];
    const companyUid = form.getFieldValue("company")?.value;
    if (companyUid) {
      conditions.push({
        field: nameOf<Department>("companyUid"),
        operation: QueryOperation.Equal,
        values: [companyUid],
      });
    }
    return retry(() =>
      getCompanyDepartments({ searchQuery: { conditions }, pageNumber: 0, pageSize: 100 }),
    ).then(({ data }) =>
      data.map((d) => ({ display: d.name, value: d.uid, dataItem: d })),
    );
  };

  // Multi-select-friendly fetcher: load all departments of a company by name prefix. Used by the
  // dedicated departments panel the dialogs render outside DynamicForm.
  const scopedDepartmentsRequest = (companyUid: string | undefined, search: string) => {
    if (!companyUid) return Promise.resolve<ParticipantItem[]>([]);
    const conditions: any[] = [
      startsWith(nameOf<Department>("name"), search),
      { field: nameOf<Department>("companyUid"), operation: QueryOperation.Equal, values: [companyUid] },
    ];
    return retry(() =>
      getCompanyDepartments({ searchQuery: { conditions }, pageNumber: 0, pageSize: 200 }),
    ).then(({ data }) =>
      data.map((d) => ({ display: d.name, value: d.uid, dataItem: d })),
    );
  };

  // The event's company (read live from the form) scopes the host/attendee pickers, mirroring the backend's
  // assignment-aware targeting: options are that company's **members + its assigned affiliates**
  // (consultants/collaborators). With no company set (a platform-wide event) the pickers fall back to
  // platform-wide — only an admin reaches that, and the backend still enforces targeting.
  const scopeCompanyUid = () => form.getFieldValue("company")?.value as string | undefined;

  // Hosts: the event's company's members + assigned affiliates; platform-wide when no company is set.
  const hostsRequest = (search: string) => {
    const companyUid = scopeCompanyUid();
    if (!companyUid)
      return retry(() =>
        getPlatformUsers({
          searchQuery: { conditions: [startsWith(nameOf<PlatformUser>("userName"), search)] },
          pageNumber: 0,
          pageSize: 100,
        }),
      ).then(({ data }) => data.map((d) => memberItem(d, EventParticipantRole.Host)));

    return companyParticipants(companyUid, search, EventParticipantRole.Host);
  };

  return {
    typesRequest,
    companiesRequest,
    departmentsRequest,
    scopedDepartmentsRequest,
    hostsRequest,
  };
}

export type CalendarEventRequests = ReturnType<typeof buildCalendarEventRequests>;

// The core event fields, identical across both dialogs. `Type`/`Description` etc. stay required;
// End must be after Start. Passing `readOnly` keeps the same input layout but disables editing — so a
// preview is just the add/edit form with its controls locked (no separate label-style rendering).
// `companyLocked` makes Company read-only even outside preview — used by MyCalendar where the admin's
// own company is prefilled and must not be changed.
// Departments are NOT part of this schema — each dialog renders its own dedicated multi-select panel
// (chips below the picker) since DynamicForm's combobox is single-value only.
export function coreEventFields(
  requests: CalendarEventRequests,
  readOnly = false,
  companyLocked = false,
): DynamicControl[] {
  return [
    {
      type: "textbox",
      label: "Title",
      name: "title",
      readOnly,
      rules: [{ required: true, message: "Title is required" }],
    },
    {
      type: "combobox",
      label: "Type",
      name: "type",
      readOnly,
      rules: [{ required: true, message: "Type is required" }],
      request: requests.typesRequest,
    },
    {
      type: "combobox",
      label: "Company",
      name: "company",
      readOnly: readOnly || companyLocked,
      request: requests.companiesRequest,
    },
    {
      type: "datetimepicker",
      label: "Start Event",
      name: "startDateTime",
      readOnly,
      rules: [{ required: true, message: "Start Event is required" }],
      format: dateTimeFormat,
    },
    {
      type: "datetimepicker",
      label: "End Event",
      name: "endDateTime",
      readOnly,
      rules: [
        { required: true, message: "End Event is required" },
        ({ getFieldValue }) => ({
          validator(_, endDateTime) {
            const startDateTime = getFieldValue("startDateTime");
            if (!endDateTime || !startDateTime || startDateTime < endDateTime) {
              return Promise.resolve();
            }
            return Promise.reject(new Error("End date cannot be less than start date"));
          },
        }),
      ],
      format: dateTimeFormat,
    },
    {
      type: "areabox",
      label: "Description",
      name: "description",
      readOnly,
      rows: 15,
      rules: [{ required: true, message: "Description is required" }],
    },
  ];
}

// Role-aware fan-out for newly-staged participants (those without an invite uid). Users go through
// InviteUser carrying their role (Host/Attendee); companies/departments fan out as attendees.
export function addEventInvitations(
  eventUid: string,
  staged: ParticipantItem[],
): Promise<ParticipantItem[]> {
  // NOTE: failures propagate (no per-invite catch) so the caller can surface them instead of silently
  // dropping a participant and closing as success.
  const promises = staged.map((target) => {
    const role = target.dataItem?.__role ?? EventParticipantRole.Attendee;
    if (target.dataItem?.__type === "company") {
      return addCalendarCompanyInvitations({ companyUid: target.value, eventUid })
        .then(() => target);
    } else if (target.dataItem?.__type === "department") {
      return addCalendarDepartmentInvitations({ departmentUid: target.value, eventUid })
        .then(() => target);
    } else if (target.dataItem?.__type === "user") {
      return addCalendarUserInvitations({ userUid: target.value, eventUid, role })
        .then(() => target);
    }
    return Promise.resolve(null);
  });

  return Promise.all(promises).then(
    (results) => results.filter((f) => f != null) as ParticipantItem[],
  );
}
